// Contour (isosurface) intent handlers.
//
// Live-preview lifecycle: while the Contours tab is open, a single ghosted
// preview surface tracks the Value/Domain (debounced, async). "Add"
// (GENERATE_CONTOUR) commits the current preview into the persistent stack;
// leaving the tab discards the un-added preview. See scene/contour.ts.

import { INTENTS } from '@store/intents';
import { params } from '@store/instance';
import {
    clearAll as cntClearAll,
    applyOpacity as cntApplyOpacity,
    applyRenderOrder as cntApplyRenderOrder,
    applyVisibility as cntApplyVisibility,
    previewNow as cntPreviewNow,
    commitPreview as cntCommitPreview,
    clearPreview as cntClearPreview,
} from '@scene/contour';
import type { Store } from '@store/store';
import type { State } from '@store/schema';
import type { ViewerContext } from '../context';

// Coalesce rapid slider drags into one worker run per quiet window.
const PREVIEW_DEBOUNCE_MS = 140;

export function register(store: Store<State>, viewer: ViewerContext): Array<() => void> {
    const offs: Array<() => void> = [];

    // Preview is only live while the Contours rail tab is open.
    let onContourTab = false;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const cancelDebounce = (): void => { if (debounce) { clearTimeout(debounce); debounce = null; } };
    const schedulePreview = (): void => {
        if (!onContourTab) return;
        cancelDebounce();
        debounce = setTimeout(() => { debounce = null; cntPreviewNow(viewer, params); }, PREVIEW_DEBOUNCE_MS);
    };

    offs.push(store.onIntent(INTENTS.CLEAR_CONTOURS, () => {
        cntClearAll(viewer);
        if (onContourTab) cntPreviewNow(viewer, params); // restore the live ghost
    }));
    offs.push(store.onIntent(INTENTS.GENERATE_CONTOUR, () => cntCommitPreview(viewer, params)));

    offs.push(store.onIntent(INTENTS.SET_CONTOUR_VISIBILITY, (p) => {
        if (!p) return;
        params.showContours = !!p.visible;
        cntApplyVisibility(viewer, !!p.visible);
        store.dispatch(INTENTS.UI_PANE_REFRESH);
    }));

    offs.push(store.onIntent(INTENTS.SET_CONTOUR_OPACITY, (p) => {
        if (!p || typeof p.value !== 'number') return;
        params.contourOpacity = p.value;
        cntApplyOpacity(viewer, p.value);
    }));

    offs.push(store.onIntent(INTENTS.SET_CONTOUR_RENDER_ORDER, (p) => {
        if (!p || typeof p.value !== 'number') return;
        params.contourRenderOrder = p.value;
        cntApplyRenderOrder(viewer, p.value);
    }));

    offs.push(store.onIntent(INTENTS.SET_CONTOUR_VALUE, (p) => {
        if (p && typeof p.value === 'number') params.contourValue = p.value;
    }));

    // Drive the preview off the committed param values (fires after the pane
    // writes them, regardless of source) rather than the intent payloads.
    offs.push(store.subscribe('params.contourValue', schedulePreview));
    offs.push(store.subscribe('params.contourDomain', schedulePreview));

    offs.push(store.onIntent(INTENTS.UI_TAB_CHANGED, (p) => {
        const onContour = !!p && p.which === 'contour';
        if (onContour === onContourTab) return;
        onContourTab = onContour;
        if (onContour) {
            cntPreviewNow(viewer, params);
        } else {
            cancelDebounce();
            cntClearPreview(viewer);
        }
    }));

    offs.push(() => cancelDebounce());

    return offs;
}
